/* eslint-disable */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService } from './login/auth.service';
import { changeWindowWith, loginUser } from './redux/actions/user.actions';
import { AppState } from './redux/app.state';
import { User } from 'src/typings';
import { DeviceTypeEnum } from 'src/enums';
import { updateCart } from './redux/actions/cart.actions';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from './services/notification.service';
import { AuthSessionCoordinatorService } from './login/auth-session-coordinator.service';
import { EscapeBackService } from './services/escape-back.service';
import { FiscalSessionSyncService } from './fiscal/fiscal-session-sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Autof CRM';

  isAuthUser = false;

  loading = false;

  sessionRestoring = false;

  user: User | undefined;

  deviceType = DeviceTypeEnum.DESKTOP;

  private readonly authNoticeStorageKey = 'auth-session-notice';

  private handlingExternalSessionChange = false;

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private router: Router,
    private notificationService: NotificationService,
    private authSessionCoordinator: AuthSessionCoordinatorService,
    private escapeBack: EscapeBackService,
    private fiscalSessionSync: FiscalSessionSyncService
  ) {
    this.store.subscribe((data) => {
      this.loading = data.user.loading;
      this.user = data.user.info;
      this.deviceType = data.user.deviceType;
    });
    window.addEventListener('storage', () => {
      const newCartState = window.localStorage.getItem('cart');
      if (newCartState) {
        try {
          const parsed = JSON.parse(newCartState);
          this.store.dispatch(updateCart({ payload: Array.isArray(parsed) ? parsed : [] }));
        } catch {
          window.localStorage.setItem('cart', JSON.stringify([]));
          this.store.dispatch(updateCart({ payload: [] }));
        }
      }
    });
  }

  getDeviceType(width: number) {
    let deviceType = DeviceTypeEnum.DESKTOP;
    if (window.innerWidth <= 600) deviceType = DeviceTypeEnum.MOBILE;
    if (window.innerWidth >= 600 && window.innerWidth <= 800) deviceType = DeviceTypeEnum.TABLET;
    if (window.innerWidth >= 800 && window.innerWidth <= 1200) deviceType = DeviceTypeEnum.LAPTOP;
    if (window.innerWidth >= 1200) deviceType = DeviceTypeEnum.DESKTOP;
    return deviceType;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    if (this.deviceType !== this.getDeviceType(window.innerWidth)) {
      this.store.dispatch(
        changeWindowWith({
          payload: { device_type: this.getDeviceType(window.innerWidth) }
        })
      );
    }
  }

  @HostListener('window:focus')
  onFocus() {
    this.ensureSessionConsistency();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.ensureSessionConsistency();
    }
  }

  async ngOnInit(): Promise<void> {
    this.escapeBack.start();
    this.fiscalSessionSync.start();
    this.showPendingAuthNotice();
    this.authSessionCoordinator.events$.subscribe(() => {
      this.ensureSessionConsistency();
    });

    this.store.dispatch(
      changeWindowWith({
        payload: { device_type: this.getDeviceType(window.innerWidth) }
      })
    );

    if (this.authService.hasAuthCookies()) {
      this.sessionRestoring = true;
      // Помечаем сессию как активную до загрузки профиля.
      this.store.dispatch(
        loginUser({
          payload: {
            info: null // временно null
          }
        })
      );

      try {
        await firstValueFrom(this.authService.restoreSession());
        this.authService.syncTrackedSessionFingerprint();
      } catch (err) {
        this.authService.logout({ broadcast: false });
      } finally {
        this.sessionRestoring = false;
      }
      return;
    }

    this.sessionRestoring = false;
  }

  ngOnDestroy(): void {
    this.escapeBack.ngOnDestroy();
    this.fiscalSessionSync.ngOnDestroy();
  }

  private ensureSessionConsistency() {
    if (this.handlingExternalSessionChange || !this.authService.hasTrackedSessionMismatch()) {
      return;
    }

    this.handlingExternalSessionChange = true;
    const hasActiveSession = this.authService.hasAuthCookies();
    this.authService.resetClientContext({
      deleteCookies: !hasActiveSession,
      broadcast: false
    });

    this.persistAuthNotice(
      hasActiveSession
        ? 'Аккаунт изменился в другой вкладке. Обновляем данные.'
        : 'Сессия завершена в другой вкладке. Войдите снова.'
    );

    if (hasActiveSession) {
      window.location.reload();
      return;
    }

    this.router.navigateByUrl('/login').finally(() => {
      this.handlingExternalSessionChange = false;
    });
  }

  private showPendingAuthNotice() {
    const message = globalThis.sessionStorage?.getItem(this.authNoticeStorageKey);

    if (!message) {
      return;
    }

    globalThis.sessionStorage?.removeItem(this.authNoticeStorageKey);
    this.notificationService.notice(message);
  }

  private persistAuthNotice(message: string) {
    globalThis.sessionStorage?.setItem(this.authNoticeStorageKey, message);
  }
}
